import * as Immutable from "immutable";
import { AuthzRolesTeams, Teams } from "@graylog/enterprise-api";
import SharedEntity from "logic/permissions/SharedEntity";
import fetch from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import Role from "logic/roles/Role";
import UserOverview from "logic/users/UserOverview";
import PaginationURL from "util/PaginationURL";
import Team from "../logic/Team";
const apiPrefix = "/plugins/org.graylog.plugins.security";
const ApiRoutes = {
  TeamsApiController: {
    addMemberToTeams: (userId) => ({ url: `${apiPrefix}/teams/${userId}/teams` }),
    create: () => ({ url: `${apiPrefix}/teams` }),
    teamSharesPaginated: (teamId) => ({ url: `${apiPrefix}/teams/shares/${teamId}` })
  }
};
export const TEAMS_QUERY_KEY = ["teams"];
const formatPaginatedSharesResponse = ({
  additional_queries: additionalQueries,
  context,
  count,
  entities,
  page,
  per_page: perPage,
  query,
  total
}) => ({
  list: Immutable.List(entities.map((se) => SharedEntity.fromJSON(se))),
  context: {
    granteeCapabilities: context.grantee_capabilities
  },
  pagination: {
    additionalQueries,
    page,
    perPage,
    query,
    count,
    total
  }
});
const _responseToPaginatedList = ({ count, total, page, per_page, query, teams, context }) => ({
  context: {
    users: Immutable.Map(context == null ? void 0 : context.users),
    roles: Immutable.Map(context == null ? void 0 : context.roles)
  },
  list: Immutable.List(teams.map(Team.fromJSON)),
  pagination: {
    page,
    perPage: per_page,
    query,
    count,
    total
  }
});
const _responseToPaginatedUserList = ({
  count,
  total,
  page,
  per_page,
  query,
  users,
  context
}) => ({
  list: Immutable.List(users.map((u) => UserOverview.fromJSON(u))),
  pagination: {
    page,
    perPage: per_page,
    query,
    count,
    total
  },
  adminUser: (context == null ? void 0 : context.admin_user) ? UserOverview.fromJSON(context == null ? void 0 : context.admin_user) : void 0
});
const _responseToPaginatedRolesList = ({
  count,
  total,
  page,
  per_page,
  query,
  roles,
  context
}) => ({
  list: Immutable.List(roles.map((r) => Role.fromJSON(r))),
  pagination: {
    page,
    perPage: per_page,
    query,
    count,
    total
  },
  context
});
export const createTeam = (team, share_request) => {
  const { url } = ApiRoutes.TeamsApiController.create();
  return fetch("POST", qualifyUrl(url), { entity: team.toJSON(), share_request }).then(Team.fromJSON);
};
export const loadTeam = (teamId) => Teams.getByteamId(teamId).then(Team.fromJSON);
export const updateTeam = (team) => Teams.update(team.id, team.toJSON()).then(Team.fromJSON);
export const deleteTeam = (team) => Teams.remove(team.id);
export const addMembers = (teamId, userIds) => Teams.addMember(teamId, userIds.toArray()).then(Team.fromJSON);
export const addMemberToTeams = (userId, teamIds) => {
  const { url } = ApiRoutes.TeamsApiController.addMemberToTeams(userId);
  return fetch("PUT", qualifyUrl(url), teamIds.toArray());
};
export const addRoleToTeams = (roleId, teamIds) => AuthzRolesTeams.addRoleToTeams(roleId, teamIds.toArray());
export const removeMember = (teamId, userId) => Teams.removeMember(teamId, userId).then(Team.fromJSON);
export const removeRole = (roleId, teamId) => AuthzRolesTeams.removeRoleFromTeam(roleId, teamId);
export const loadRolesForTeam = (teamId, _teamName, { page, perPage, query }) => Teams.getListOfRoles(void 0, teamId, page, perPage, query).then(_responseToPaginatedRolesList);
export const loadUsersForTeam = (teamId, _teamName, { page, perPage, query }) => Teams.getListOfUsers(void 0, teamId, page, perPage, query).then(_responseToPaginatedUserList);
export const loadTeamsForUser = (userId, { page, perPage, query }) => Teams.getListForUsername(void 0, userId, page, perPage, query).then(_responseToPaginatedList);
export const loadTeamsForRole = (roleId, { page, perPage, query }) => AuthzRolesTeams.getUsersForRole(void 0, roleId, page, perPage, query).then(_responseToPaginatedList);
export const loadTeamsPaginated = ({ page, perPage, query }) => Teams.getList(void 0, page, perPage, query).then(_responseToPaginatedList);
export const loadTeamSharesPaginated = (teamId, { page, perPage, query, additionalQueries }) => {
  const url = PaginationURL(
    ApiRoutes.TeamsApiController.teamSharesPaginated(teamId).url,
    page,
    perPage,
    query,
    additionalQueries
  );
  return fetch("GET", qualifyUrl(url)).then(formatPaginatedSharesResponse);
};
